import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, Loader2 } from "lucide-react";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import LevelMeaning from "@/components/LevelMeaning";
import { toast } from "@/components/ui/sonner";
import {
  ATTENDANCE_LABELS, OBJECTIVE_STATUS_COLORS, OBJECTIVE_STATUS_LABELS,
  PROGRAMME_STATUS_LABELS, SKILL_AREAS,
} from "@/config/trainingCatalog";
import { ProgrammeStatusBadge } from "@/components/child/TrainingTab";

const CHART_COLORS = ["#005EB8", "#009639", "#ED8B00", "#DA291C", "#7C2855", "#00A499", "#330072", "#768692", "#003087"];

export default function ProgressTab({ child }) {
  const [programme, setProgramme] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/programmes");
        const mine = (data.items || []).filter((p) => p.child_id === child.id);
        const active = mine.find((p) => !["completed", "cancelled", "archived"].includes(p.status)) || mine[0];
        if (!active) {
          setLoading(false);
          return;
        }
        const [detail, prog] = await Promise.all([
          api.get(`/programmes/${active.id}`),
          api.get(`/programmes/${active.id}/progress`),
        ]);
        setProgramme(detail.data);
        setProgress(prog.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [child.id]);

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-600" data-testid="progress-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading progress…</div>;
  }
  if (!programme) {
    return (
      <Card data-testid="progress-empty"><CardContent className="p-6">
        <p className="text-sm text-slate-600">No training programme yet — progress tracking begins once a programme is created.</p>
      </CardContent></Card>
    );
  }

  const levels = progress?.skill_levels || {};
  const levelNames = Object.fromEntries((progress?.independence_levels || []).map((l) => [l.level, l.name]));
  const attendance = progress?.attendance || {};
  const chartData = (progress?.weeks || []).map((w) => ({ week: `W${w.week}`, ...w.areas }));
  const chartAreas = [...new Set((progress?.weeks || []).flatMap((w) => Object.keys(w.areas)))];
  const daysSinceStart = programme.start_date
    ? Math.max(0, Math.floor((Date.now() - new Date(programme.start_date).getTime()) / 86400000) + 1)
    : null;

  return (
    <div className="space-y-4" data-testid="progress-tab">
      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Activity className="h-5 w-5 text-primary" /> Programme progress</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><dt className="text-xs text-slate-500">Programme</dt><dd className="font-medium"><Link to={`/programmes/${programme.id}`} className="text-primary hover:underline" data-testid="progress-programme-link">{programme.programme_number}</Link></dd></div>
            <div><dt className="text-xs text-slate-500">Status</dt><dd><ProgrammeStatusBadge status={programme.status} testid="progress-status" /></dd></div>
            <div><dt className="text-xs text-slate-500">Current week</dt><dd className="font-medium" data-testid="progress-week">{programme.current_week ? `Week ${programme.current_week} of ${programme.planned_weeks}` : "Not started"}</dd></div>
            <div><dt className="text-xs text-slate-500">Programme day</dt><dd className="font-medium" data-testid="progress-day">{daysSinceStart ? `Day ${daysSinceStart}` : "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Trainer</dt><dd className="font-medium">{programme.trainer_name || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Target completion</dt><dd className="font-medium">{programme.target_completion_date || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Sessions submitted</dt><dd className="font-medium" data-testid="progress-sessions">{progress?.sessions_submitted ?? 0}</dd></div>
            <div><dt className="text-xs text-slate-500">Interventions</dt><dd className="font-medium" data-testid="progress-interventions">{progress?.interventions ?? 0}</dd></div>
          </dl>
          {Object.keys(attendance).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="progress-attendance">
              {Object.entries(attendance).map(([k, v]) => (
                <Badge key={k} className={k === "completed" ? "bg-ukgreen-light text-ukgreen border-transparent" : "bg-slate-100 text-slate-600 border-transparent"}>
                  {ATTENDANCE_LABELS[k] || k}: {v}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Current independence levels (per direction)</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="progress-levels-table">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-border">
                <th className="py-2 pr-3">Skill area</th><th className="py-2 pr-3">Home → School</th><th className="py-2">School → Home</th>
              </tr></thead>
              <tbody>
                {SKILL_AREAS.map((a) => {
                  const lv = levels[a.key] || {};
                  return (
                    <tr key={a.key} className="border-b border-border/50" data-testid={`progress-level-${a.key}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{a.label}</td>
                      <td className="py-2 pr-3">
                        {lv.outbound ? (
                          <div>
                            <Badge className="bg-blue-50 text-blue-700 border-transparent">L{lv.outbound} {levelNames[lv.outbound] || ""}</Badge>
                            <LevelMeaning levels={progress?.independence_levels} level={lv.outbound} testid={`progress-outbound-meaning-${a.key}`} />
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-2">
                        {lv.return ? (
                          <div>
                            <Badge className="bg-blue-50 text-blue-700 border-transparent">L{lv.return} {levelNames[lv.return] || ""}</Badge>
                            <LevelMeaning levels={progress?.independence_levels} level={lv.return} testid={`progress-return-meaning-${a.key}`} />
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Progress over time (recorded session evidence)</CardTitle></CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="progress-chart-empty">No completed sessions recorded yet.</p>
          ) : (
            <div className="h-72" data-testid="progress-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 16, bottom: 5, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} />
                  <Tooltip />
                  <Legend />
                  {chartAreas.map((area, i) => (
                    <Line key={area} type="monotone" dataKey={area} name={SKILL_AREAS.find((a) => a.key === area)?.label || area}
                      stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <p className="mt-2 text-xs text-slate-500">
                Scale 0–5 reflects average recorded support need (5 = independent). Charts present evidence only — they do
                not determine outcomes; professional assessment does. A dip is information, not failure.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Training objectives ({progress?.objectives?.achieved ?? 0}/{progress?.objectives?.total ?? 0} achieved)</CardTitle></CardHeader>
        <CardContent>
          {(programme.objectives || []).length === 0 ? (
            <p className="text-sm text-slate-600">No objectives recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {programme.objectives.map((o) => (
                <li key={o.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`progress-objective-${o.id}`}>
                  <span className="flex-1 text-slate-800">{o.objective}</span>
                  <Badge className={OBJECTIVE_STATUS_COLORS[o.status] || ""}>{OBJECTIVE_STATUS_LABELS[o.status] || o.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent sessions</CardTitle></CardHeader>
        <CardContent>
          {(programme.sessions || []).filter((s) => s.status === "submitted").length === 0 ? (
            <p className="text-sm text-slate-600">No submitted sessions yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {programme.sessions.filter((s) => s.status === "submitted").slice(0, 8).map((s) => (
                <li key={s.id} data-testid={`progress-session-${s.id}`}>
                  <Link to={`/sessions/${s.id}`} className="flex flex-wrap items-center gap-2 py-2.5 text-sm hover:bg-muted rounded px-1 transition-colors duration-200">
                    <span className="font-medium text-slate-800 w-24">{s.date}</span>
                    <span className="text-slate-600">{s.direction === "outbound" ? "Home → School" : s.direction === "return" ? "School → Home" : "Other"}</span>
                    <span className="text-slate-500">L{s.training_level || "—"}</span>
                    <Badge className={s.attendance === "completed" ? "bg-ukgreen-light text-ukgreen border-transparent" : "bg-amber-100 text-amber-800 border-transparent"}>
                      {ATTENDANCE_LABELS[s.attendance] || s.attendance || "—"}
                    </Badge>
                    {s.has_intervention && (
                      <Badge className="bg-red-50 text-red-700 border-transparent"><AlertTriangle className="h-3 w-3 mr-1" />Intervention</Badge>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
